import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import { getApiBaseUrl } from '../../../core/config/api';
import { logout } from '../../../core/store/slices/userSlice';
import SessionService from '../../../shared/services/sessionService';
import { usePanelAccess } from '../context/PanelAccessContext';
import PanelSidebarNav from '../components/PanelSidebarNav';
import { ROLE_LABELS, resolvePageTitle } from '../config/panelNavConfig';

const PanelLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const roleId = currentUser?.roles_id ?? 0;
  const { loading: accessLoading, hasAccessToSection } = usePanelAccess();
  const pageTitle = resolvePageTitle(location.pathname);
  const isDashboard = location.pathname.startsWith('/panel/dashboard') || location.pathname === '/panel';

  const [serverOnline, setServerOnline] = React.useState(true);
  const [counters, setCounters] = React.useState({ joueurs: 0, interventions: 0, signalements: 0 });

  const displayRole = currentUser?.roles_id != null ? ROLE_LABELS[currentUser.roles_id] || `Rôle #${currentUser.roles_id}` : 'Inconnu';
  const displayName = currentUser?.username || 'Utilisateur';

  React.useEffect(() => {
    const run = async () => {
      try {
        const healthRes = await fetch(`${getApiBaseUrl(true)}/health`);
        setServerOnline(healthRes.ok);
      } catch {
        setServerOnline(false);
      }
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/dashboard/overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data?.success && data?.data?.counters) {
          setCounters({
            joueurs: Number(data.data.counters.joueurs_en_ligne || 0),
            interventions: Number(data.data.counters.interventions_actives || 0),
            signalements: Number(data.data.counters.signalements_attente || 0),
          });
        }
      } catch {
        // ignore
      }
    };
    void run();
  }, []);

  const handleLogout = async () => {
    try {
      await SessionService.getInstance().logout();
    } catch {
      // ignore
    } finally {
      dispatch(logout());
      localStorage.removeItem('token');
      navigate('/');
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden font-inter text-cockpit-text">
      <div className="pointer-events-none fixed inset-0 z-0">
        <BackgroundImage />
        <ParticlesBackground />
        <div className="absolute inset-0 bg-cockpit-bg/60" aria-hidden />
      </div>

      <Navbar />

      <div className="relative z-10 flex min-h-screen pt-16">
        <aside className="fixed left-0 top-16 z-20 flex h-[calc(100vh-4rem)] w-[260px] flex-col border-r border-cockpit-line/70 bg-cockpit-bg-2/70 backdrop-blur-md">
          <PanelSidebarNav roleId={roleId} accessLoading={accessLoading} hasAccessToSection={hasAccessToSection} />

          <div className="shrink-0 border-t border-cockpit-line/70 p-3">
            <div className="rounded-md border border-cockpit-line/70 bg-cockpit-panel/70 px-3 py-2.5 backdrop-blur-sm">
              <p className="truncate text-sm font-medium text-cockpit-text">{displayName}</p>
              <p className="truncate text-[11px] uppercase tracking-[0.08em] text-cockpit-muted">{displayRole}</p>
              <p className="mt-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-cockpit-green">
                <span className="h-1.5 w-1.5 rounded-full bg-cockpit-green shadow-[0_0_7px_rgba(16,185,129,0.9)]" />
                En ligne
              </p>
            </div>
          </div>
        </aside>

        <div className="ml-[260px] flex min-h-[calc(100vh-4rem)] flex-1 flex-col">
          <header className="sticky top-16 z-10 flex h-14 shrink-0 items-center justify-between border-b border-cockpit-line/70 bg-cockpit-bg-2/70 px-6 backdrop-blur-md">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-cockpit-muted">Section</p>
              <h1 className="text-sm font-semibold text-cockpit-text">{pageTitle}</h1>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`hidden rounded-md border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] sm:inline ${
                  serverOnline
                    ? 'border-cockpit-green/40 bg-cockpit-green/10 text-cockpit-green'
                    : 'border-cockpit-red/40 bg-cockpit-red/10 text-cockpit-red'
                }`}
              >
                {serverOnline ? 'Serveur OK' : 'Serveur HS'}
              </span>
              <Link
                to="/dashboard"
                className="rounded-md border border-cockpit-line/70 bg-cockpit-panel/60 px-3 py-1.5 text-[11px] font-medium text-cockpit-text-2 transition-colors hover:border-cockpit-blue/50 hover:text-cockpit-text"
              >
                Jeu
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="rounded-md border border-cockpit-red/40 bg-cockpit-red/10 px-3 py-1.5 text-[11px] font-medium text-cockpit-red transition-colors hover:bg-cockpit-red/20"
              >
                Déconnexion
              </button>
            </div>
          </header>

          {!isDashboard ? (
            <div className="shrink-0 border-b border-cockpit-line/70 bg-cockpit-bg/40 px-6 py-2 backdrop-blur-sm">
              <div className="flex flex-wrap gap-4 text-[11px] uppercase tracking-[0.06em] text-cockpit-muted">
                <span>
                  Joueurs <strong className="font-mono text-cockpit-text">{counters.joueurs}</strong>
                </span>
                <span>
                  Interventions <strong className="font-mono text-cockpit-text">{counters.interventions}</strong>
                </span>
                <span>
                  Signalements <strong className="font-mono text-cockpit-amber">{counters.signalements}</strong>
                </span>
              </div>
            </div>
          ) : null}

          <main className="flex-1 px-6 py-6">
            <div className={isDashboard ? '' : 'mx-auto max-w-7xl'}>
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PanelLayout;
